import { motion } from "framer-motion";
import { PurchaseButton } from "./PurchaseButton";
import usePurchase from "@/hooks/usePurchase";
import { LineChart, ShieldCheck, BookOpen } from "lucide-react";

const TICKERS = [
  "BTC/USD +2.34%",
  "ES1! +0.42%",
  "AAPL +1.18%",
  "EUR/USD -0.21%",
  "NQ1! +0.87%",
  "TSLA +3.05%",
  "GOLD +0.55%",
  "SPY +0.39%",
  "MSFT +1.62%",
  "NVDA +4.10%",
];

export default function Hero() {
  const { purchase, loading } = usePurchase();
  return (
    <section
      id="top"
      className="relative overflow-hidden pt-28 md:pt-36 pb-20 md:pb-28"
    >
      {/* Background grid */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px,transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />
      {/* Emerald glow */}
      <div
        aria-hidden
        className="absolute -z-10 left-1/2 -translate-x-1/2 -top-40 w-[900px] h-[900px] rounded-full bg-emerald-500/20 blur-[140px]"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          {/* Left — text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-left"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 text-xs font-medium tracking-wider uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              New 2026 Edition · Beginner-Friendly
            </div>

            <h1 className="font-display font-extrabold tracking-tighter leading-[1.02] text-white text-5xl sm:text-6xl lg:text-7xl">
              Master the Basics of <br className="hidden md:block" />
              <span className="text-emerald-400 text-glow-emerald">Day Trading</span>{" "}
              Step-by-Step
            </h1>

            <p className="mt-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
              Learn how the markets work, understand trading terminology, build strategies,
              and develop better trading habits with this beginner-friendly visual guide.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-5">
              <PurchaseButton
                testId="hero-purchase-btn"
                onClick={() => purchase()}
                loading={loading}
                size="xl"
                showPrice
              />
              <a
                href="#inside"
                data-testid="hero-preview-link"
                className="text-sm text-white/60 hover:text-white transition underline underline-offset-4"
              >
                Peek inside the book →
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-xs text-white/50">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Instant Digital Access
              </span>
              <span className="inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-emerald-400" />
                Read on Any Device
              </span>
              <span className="inline-flex items-center gap-2">
                <LineChart className="w-4 h-4 text-emerald-400" />
                180+ Pages · 60+ Charts
              </span>
            </div>
          </motion.div>

          {/* Right — book mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
            animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="relative mx-auto"
          >
            <BookMockup />
          </motion.div>
        </div>
      </div>

      {/* Ticker */}
      <div className="mt-20 md:mt-24 border-y border-white/10 bg-black/40 overflow-hidden">
        <div className="ticker-track flex gap-12 py-4 whitespace-nowrap font-display font-semibold uppercase tracking-widest text-sm">
          {[...TICKERS, ...TICKERS].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function BookMockup() {
  return (
    <div className="relative animate-floaty">
      {/* Glow */}
      <div className="absolute -inset-10 bg-emerald-500/30 blur-3xl rounded-full -z-10" />
      <img
        src="https://customer-assets.emergentagent.com/job_day-trade-ebook-1/artifacts/j7ai7fq4_ChatGPT%20Image%20Jun%2029%2C%202026%2C%2011_24_53%20PM.png"
        alt="The 9-to-5 Exit Blueprint — book cover"
        className="relative w-[280px] sm:w-[340px] md:w-[400px] lg:w-[440px] h-auto object-contain drop-shadow-[0_40px_60px_rgba(0,0,0,0.7)]"
      />
    </div>
  );
}
